import { useSyncExternalStore } from "react";
import type { BadgeCategory } from "../hooks/useUnseenBadges";

export type NavIconName = "home" | "calendar" | "list" | "check" | "star" | "note" | "mood" | "piggy" | "link";

export interface NavItemDef {
  id: string;
  to: string;
  label: string;
  icon: NavIconName;
  end?: boolean;
  badge?: BadgeCategory;
}

// Every page that could plausibly live in the bottom nav. Order here is
// also the order shown in the "Startseite anpassen" picker (Home.tsx) —
// a chosen item's position in the bar itself follows selection order, not
// this list's order (see setBottomNavIds).
export const ALL_NAV_ITEMS: NavItemDef[] = [
  { id: "home", to: "/", label: "Übersicht", icon: "home", end: true },
  { id: "calendar", to: "/calendar", label: "Kalender", icon: "calendar", badge: "calendar" },
  { id: "lists", to: "/lists", label: "Listen", icon: "list", badge: "lists" },
  { id: "todos", to: "/todos", label: "Todos", icon: "check", badge: "todos" },
  { id: "dates", to: "/dates", label: "Wichtige Daten", icon: "star" },
  { id: "notes", to: "/notes", label: "Notizen", icon: "note" },
  { id: "mood", to: "/mood", label: "Stimmung", icon: "mood" },
  { id: "savings", to: "/savings", label: "Sparziele", icon: "piggy" },
  { id: "links", to: "/links", label: "Links", icon: "link" },
];

const STORAGE_KEY = "wir_bottom_nav_items";
const DEFAULT_IDS = ["home", "calendar", "lists", "todos"];
export const MIN_ITEMS = 2;
export const MAX_ITEMS = 4;

// Per-device, like the Home.tsx widget picker and the accent color choice —
// each partner can lay out their own bottom nav differently.
function loadIds(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_IDS;
    const arr = JSON.parse(raw) as string[];
    const valid = arr.filter((id) => ALL_NAV_ITEMS.some((it) => it.id === id));
    return valid.length >= MIN_ITEMS ? valid : DEFAULT_IDS;
  } catch {
    return DEFAULT_IDS;
  }
}

function computeItems(ids: string[]): NavItemDef[] {
  return ids.map((id) => ALL_NAV_ITEMS.find((it) => it.id === id)).filter((it): it is NavItemDef => !!it);
}

let currentIds = loadIds();
let currentItems = computeItems(currentIds);

type Listener = () => void;
const listeners = new Set<Listener>();

function getIdsSnapshot() {
  return currentIds;
}
function getItemsSnapshot() {
  return currentItems;
}

// localStorage's own "storage" event only fires in *other* tabs, not the one
// that wrote it — this small pub/sub is what lets BottomNav re-render the
// moment Home.tsx's picker changes the selection, in the same tab.
export function setBottomNavIds(ids: string[]) {
  currentIds = ids;
  currentItems = computeItems(ids);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  listeners.forEach((l) => l());
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useBottomNavIds(): string[] {
  return useSyncExternalStore(subscribe, getIdsSnapshot);
}

export function useBottomNavItems(): NavItemDef[] {
  return useSyncExternalStore(subscribe, getItemsSnapshot);
}
